import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
  Grid,
} from '@mui/material';
import attendeeFollowUpReminderService, {
  type AttendeeFollowUpReminder,
  type CreateAttendeeFollowUpReminderData,
} from '../services/attendeeFollowUpReminderService';

interface AttendeeFollowUpReminderDialogProps {
  open: boolean;
  onClose: () => void;
  agencySlug: string;
  attendeeName: string;
  attendeePhone: string;
  attendeeEmail?: string;
  propertyId?: string;
  existingReminder?: AttendeeFollowUpReminder;
  onSuccess?: () => void;
}

const isoToLocalDateTime = (isoString: string): string => {
  const date = new Date(isoString);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

const localDateTimeToIso = (localDateTime: string): string => {
  const [datePart, timePart] = localDateTime.split('T');
  const [year, month, day] = datePart.split('-').map(Number);
  const [hours, minutes] = timePart.split(':').map(Number);
  return new Date(year, month - 1, day, hours, minutes).toISOString();
};

const AttendeeFollowUpReminderDialog: React.FC<AttendeeFollowUpReminderDialogProps> = ({
  open,
  onClose,
  agencySlug,
  attendeeName,
  attendeePhone,
  attendeeEmail,
  propertyId,
  existingReminder,
  onSuccess,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState<CreateAttendeeFollowUpReminderData>({
    attendeePhone,
    attendeeName,
    attendeeEmail,
    propertyId,
    title: `Follow up with ${attendeeName}`,
    description: '',
    reminderTime: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
    isRecurring: false,
    notificationType: 'in_app',
    priority: 'medium',
  });
  const [recurringPattern, setRecurringPattern] = useState({
    type: 'weekly' as 'daily' | 'weekly' | 'monthly',
    interval: 1,
    endDate: '',
  });

  useEffect(() => {
    if (existingReminder) {
      setFormData({
        attendeePhone: existingReminder.attendeePhone,
        attendeeName: existingReminder.attendeeName,
        attendeeEmail: existingReminder.attendeeEmail,
        propertyId: existingReminder.propertyId,
        title: existingReminder.title,
        description: existingReminder.description || '',
        reminderTime: existingReminder.reminderTime,
        isRecurring: existingReminder.isRecurring,
        notificationType: existingReminder.notificationType,
        priority: existingReminder.priority,
      });
      if (existingReminder.recurringPattern) {
        setRecurringPattern({
          type: existingReminder.recurringPattern.type,
          interval: existingReminder.recurringPattern.interval,
          endDate: existingReminder.recurringPattern.endDate || '',
        });
      }
    } else {
      setFormData({
        attendeePhone,
        attendeeName,
        attendeeEmail,
        propertyId,
        title: `Follow up with ${attendeeName}`,
        description: '',
        reminderTime: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
        isRecurring: false,
        notificationType: 'in_app',
        priority: 'medium',
      });
      setRecurringPattern({ type: 'weekly', interval: 1, endDate: '' });
    }
  }, [existingReminder, attendeeName, attendeePhone, attendeeEmail, propertyId, open]);

  const handleSubmit = async () => {
    setLoading(true);
    setError(null);

    try {
      const submitData = {
        ...formData,
        ...(formData.isRecurring && {
          recurringPattern: {
            type: recurringPattern.type,
            interval: recurringPattern.interval,
            ...(recurringPattern.endDate && { endDate: recurringPattern.endDate }),
          },
        }),
      };

      if (existingReminder) {
        await attendeeFollowUpReminderService.updateAttendeeFollowUpReminder(
          agencySlug,
          existingReminder._id,
          submitData
        );
      } else {
        await attendeeFollowUpReminderService.createAttendeeFollowUpReminder(agencySlug, submitData);
      }

      onSuccess?.();
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to save reminder');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelReminder = async () => {
    if (!existingReminder) return;
    setLoading(true);
    setError(null);
    try {
      await attendeeFollowUpReminderService.deleteAttendeeFollowUpReminder(
        agencySlug,
        existingReminder._id
      );
      onSuccess?.();
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to cancel reminder');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {existingReminder ? 'Edit Follow-up Reminder' : 'Set Follow-up Reminder'}
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Attendee: {attendeeName}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {attendeePhone}{attendeeEmail ? ` · ${attendeeEmail}` : ''}
          </Typography>
        </Box>

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Reminder Title"
              value={formData.title}
              onChange={(e) => setFormData((prev) => ({ ...prev, title: e.target.value }))}
              required
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Notes"
              value={formData.description}
              onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
              multiline
              rows={2}
              placeholder="What to follow up on?"
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Reminder Time"
              type="datetime-local"
              value={isoToLocalDateTime(formData.reminderTime)}
              onChange={(e) => {
                if (e.target.value) {
                  setFormData((prev) => ({
                    ...prev,
                    reminderTime: localDateTimeToIso(e.target.value),
                  }));
                }
              }}
              required
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={6}>
            <FormControl fullWidth>
              <InputLabel>Priority</InputLabel>
              <Select
                value={formData.priority}
                label="Priority"
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, priority: e.target.value as any }))
                }
              >
                <MenuItem value="low">Low</MenuItem>
                <MenuItem value="medium">Medium</MenuItem>
                <MenuItem value="high">High</MenuItem>
                <MenuItem value="urgent">Urgent</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={6}>
            <FormControl fullWidth>
              <InputLabel>Notify via</InputLabel>
              <Select
                value={formData.notificationType}
                label="Notify via"
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, notificationType: e.target.value as any }))
                }
              >
                <MenuItem value="in_app">In-App</MenuItem>
                <MenuItem value="email">Email</MenuItem>
                <MenuItem value="both">Email & In-App</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isRecurring}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, isRecurring: e.target.checked }))
                  }
                />
              }
              label="Repeat reminder"
            />
          </Grid>

          {formData.isRecurring && (
            <>
              <Grid item xs={4}>
                <FormControl fullWidth>
                  <InputLabel>Repeat</InputLabel>
                  <Select
                    value={recurringPattern.type}
                    label="Repeat"
                    onChange={(e) =>
                      setRecurringPattern((prev) => ({
                        ...prev,
                        type: e.target.value as 'daily' | 'weekly' | 'monthly',
                      }))
                    }
                  >
                    <MenuItem value="daily">Daily</MenuItem>
                    <MenuItem value="weekly">Weekly</MenuItem>
                    <MenuItem value="monthly">Monthly</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={4}>
                <TextField
                  fullWidth
                  label="Every"
                  type="number"
                  value={recurringPattern.interval}
                  onChange={(e) =>
                    setRecurringPattern((prev) => ({
                      ...prev,
                      interval: parseInt(e.target.value, 10) || 1,
                    }))
                  }
                  inputProps={{ min: 1 }}
                />
              </Grid>

              <Grid item xs={4}>
                <TextField
                  fullWidth
                  label="End Date (optional)"
                  type="date"
                  value={recurringPattern.endDate}
                  onChange={(e) =>
                    setRecurringPattern((prev) => ({ ...prev, endDate: e.target.value }))
                  }
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        {existingReminder && (
          <Button onClick={handleCancelReminder} disabled={loading} color="error" sx={{ mr: 'auto' }}>
            Cancel Reminder
          </Button>
        )}
        <Button onClick={onClose} disabled={loading}>
          Close
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || !formData.title || !formData.reminderTime}
        >
          {loading ? <CircularProgress size={20} /> : existingReminder ? 'Update' : 'Set Reminder'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AttendeeFollowUpReminderDialog;
